
    import Vue from "vue";
    import Component from "vue-class-component";
    import { Prop, Ref, Watch } from "vue-property-decorator";
    import { VForm } from "@/vForm";
    import * as toastr from "toastr";
    import SlideUpDown from "vue-slide-up-down";
    import ApiClient from "@/utilities/ApiClient";
    import Utils from "@/utilities/Utils";
    import { mapData } from "@/utilities/DataMapping";
    import ApiButton from "@/vue/components/ApiButton.vue";
    import ConfirmDialogue from "@/vue/components/ConfirmDialogue.vue";
    import DatePicker from "@/vue/components/DatePicker.vue";
    import { ISaveResponse } from "@/model/ISaveResponse";
    import { ISiteRecipient, SiteRecipient } from "@/model/SiteRecipient";
    import { IBriefingPathway } from "@/model/BriefingPathway";
import { VListItemContent } from "vuetify/lib";


    class PathwayOption {
        constructor(data: IBriefingPathway, recipient: SiteRecipient) {
            mapData(data, { root: () => this });
            this.siteRecipient = recipient;
        }
        private siteRecipient: SiteRecipient;
        siteID: string = Utils.emptyGuidValue;
        briefingID: string = Utils.emptyGuidValue;
        pathwayID: string = Utils.emptyGuidValue;
        briefingName: string = "";
        pathwayDescription: string = ""
        //private myIsSelected: boolean = false;
        get isSelected() {
            return (this.siteRecipient?.pathwayIDs ?? []).includes(this.pathwayID);
        }
        set isSelected(value: boolean) {
            if(!this.siteRecipient) return;
            const index = this.siteRecipient.pathwayIDs.indexOf(this.pathwayID);
            if(!value && index > -1) {
                this.siteRecipient.pathwayIDs.splice(index, 1);
            }
            else if(value && index === -1) {
                this.siteRecipient.pathwayIDs.push(this.pathwayID);
            }
        }
    }

    @Component({ components: { ApiButton, ConfirmDialogue, DatePicker, SlideUpDown } })
    export default class SiteRecipientDialogue extends Vue {

        //
        // -- properties
        //

        @Prop({ required: true }) pathwayList!: Array<IBriefingPathway>;
        @Ref("siteRecipientForm") private readonly siteRecipientForm!: VForm;
        showDialogue: boolean = false;
        dialogueTitle: string = "";
        siteRecipient: SiteRecipient = new SiteRecipient();
        get canDelete(): boolean { return !Utils.isEmptyId(this.siteRecipient.id); }

        get pathwayOptions(): Array<PathwayOption> {
            return this.pathwayList.map(p => new PathwayOption(p, this.siteRecipient));
        }

        get pathwaysSelectedCount(): number {
            return this.siteRecipient.pathwayIDs.length;
        }

        get showPathways(): boolean {
            return this.showDialogue && (this.siteRecipient.pathwayIDs.length > 0 || !this.siteRecipient.isCarbonCopyAllPathways);
        }



        get emailCount(): string {
            return String(Utils.splitDelimited(this.siteRecipient.email).length);
        }

        @Watch("siteRecipient.isCarbonCopyAllPathways", { deep: false, immediate: false })
        onIsCarbonCopyAllPathways() {
            // clear out selections
            if(this.siteRecipient.isCarbonCopyAllPathways) {
                //this.siteRecipient.pathwayIDs.length = 0;
                this.siteRecipient.pathwayIDs.splice(0)
            }
        }

        add(siteID: string): void {
            this.reset();
            this.siteRecipient.siteID = siteID;
            this.dialogueTitle = "Add Recipient";
            this.showDialogue = true;
        }

        async edit(id: string): Promise<void> {
            this.reset();
            this.dialogueTitle = "Edit Recipient";
            const siteRecipientData: ISiteRecipient = await ApiClient.get(`api/siteRecipient/Load?id=${id}`);
            this.siteRecipient.update(siteRecipientData);
            this.showDialogue = true;
        }

        reset(): void {
            Utils.resetObject(this.siteRecipient);
            this.siteRecipient.isActive = true;
            this.siteRecipientForm?.resetValidation();
        }

        validateEmails(email: string): string|boolean {
            const list = Utils.splitDelimited(this.siteRecipient.email);
            if(list.length === 0) return "Please supply an email address";
            if(list.length === 1) {
                return Utils.isValidEmail(list[0]) ? true : "The email address is not valid";
            }
            let i = 0;
            for (const email of list) {
                const n = i + 1;
                // nb - Utils.nth() was coded for dates and will bork if we have more than 31 emails!
                //    - only validating one at a time here.  F*** it.
                if(!Utils.isValidEmail(list[i])) return `The ${n}${Utils.nth(n)} email is not valid: "${email}"`;
                i++;
            }
            return true;
        }

        async save(): Promise<void> {
            if (!this.siteRecipientForm.validate()) {
                toastr.warning("Please fix highlighted issues", "Cannot Save");
                return;
            }
            const response: ISaveResponse = await ApiClient.post("/api/siteRecipient/save", this.siteRecipient, "site-recipient-save");
            if(this.siteRecipient.isNew) this.siteRecipient.id = response.newID;
            toastr.success("Saved");
            this.$emit("saved");
            this.showDialogue = false;
        }

        confirmDelete(): void {
            const dialog: ConfirmDialogue = this.$refs.deleteDialogue as ConfirmDialogue;
            dialog.show();
        }

        async doDelete(): Promise<void> {
            await ApiClient.post("/api/siteRecipient/delete", this.siteRecipient, "siteRecipient-delete");
            toastr.warning("Deleted");
            this.$emit("saved");
            this.showDialogue = false;
        }
    }
